import { z } from "zod";
import { GameTypeSchema } from "./games.js";
// ---------------------------------------------------------------------------
// Lot v13 « Identité » — photo de profil (BE-11/FE-5) et fiche contact
// (BE-12/FE-6). Schémas partagés client/serveur.
// ---------------------------------------------------------------------------
/**
 * Les deux URL servantes d'une photo.
 *
 * Elles ne portent NI signature NI expiration, contrairement aux pièces
 * jointes : le chemin contient un identifiant de photo opaque, régénéré à chaque
 * changement, et l'autorisation se fait en amont (`POST /v1/avatars/resolve`).
 * Conséquence utile : l'URL est stable tant que la photo ne change pas, donc
 * réellement cacheable — une URL signée à TTL court annulerait le cache HTTP
 * d'une image rendue à chaque ligne de liste.
 *
 * `thumbUrl` (128 px) est la variante des listes ; `url` (512 px) est réservée à
 * la fiche et à l'édition du Compte.
 */
export const AvatarUrlsSchema = z.object({
    url: z.string().url(),
    thumbUrl: z.string().url(),
});
/** Corps de POST /v1/avatars/resolve — les comptes dont on affiche le nom. */
export const ResolveAvatarsInputSchema = z.object({
    userIds: z.array(z.string().uuid()).min(1).max(200),
});
/**
 * Réponse de POST /v1/avatars/resolve.
 *
 * Un identifiant ABSENT de `avatars` signifie « pas de photo à afficher » — et
 * couvre indistinctement : aucune photo, compte banni, relation de blocage. Le
 * client retombe alors sur la pastille d'initiales, sans savoir laquelle des
 * trois raisons s'applique (pas d'oracle de blocage).
 */
export const ResolveAvatarsResponseSchema = z.object({
    avatars: z.record(z.string(), AvatarUrlsSchema),
});
/** Réponse de PUT /v1/me/avatar. */
export const UploadAvatarResponseSchema = z.object({
    avatar: AvatarUrlsSchema,
    updatedAt: z.string(),
});
/**
 * Statut d'un compte, du point de vue d'une fiche.
 *
 * - `active` : fiche complète (dans les limites de ce que la personne partage) ;
 * - `guest`  : compte INVITÉ éphémère (G5) — fiche minimale, il n'y a rien à
 *   montrer et rien à partager ;
 * - `banned` : compte suspendu — fiche minimale, actions réduites.
 */
export const UserCardStatusSchema = z.enum(["active", "guest", "banned"]);
/** Relation de l'appelant à la personne — décide de la visibilité des coordonnées. */
export const UserCardRelationSchema = z.enum(["self", "contact", "none"]);
/**
 * Ce que la personne a CHOISI de partager.
 *
 * Absence de clé = rien à afficher. Ces champs n'apparaissent que pour un
 * contact, et `phone`/`email` exigent EN PLUS `revealPhoneToContacts` /
 * `revealEmailToContacts` (AGENTS.md §4) — un contact confirmé ne suffit pas.
 */
export const UserCardSharedSchema = z.object({
    phone: z.string().optional(),
    email: z.string().optional(),
    instagram: z.string().optional(),
    tiktok: z.string().optional(),
    whatsapp: z.string().optional(),
});
/** Points et « rang » (= niveau) — libellés seuls, aucun classement comparatif. */
export const UserCardPointsSchema = z.object({
    balance: z.number().int(),
    level: z.number().int(),
    levelName: z.string(),
});
/**
 * Réponse de GET /v1/users/:id/card.
 *
 * Tout ce qui est optionnel est ABSENT plutôt que null quand l'appelant n'y a
 * pas droit : le client ne peut pas distinguer « pas de valeur » de « pas
 * autorisé », et un champ oublié côté serveur ne peut pas fuiter en `null`
 * parlant.
 *
 * `lastSeenAt` n'est présent que si la réciprocité de `showOnlineStatus` est
 * satisfaite dans les DEUX sens (source de vérité : services/presence.ts).
 *
 * Une personne bloquée, ou qui a bloqué l'appelant, ne produit pas cette forme :
 * la route répond 404, indistinguable d'un compte inexistant.
 */
export const UserCardSchema = z.object({
    id: z.string().uuid(),
    displayName: z.string(),
    status: UserCardStatusSchema,
    relation: UserCardRelationSchema,
    handle: z.string().optional(),
    avatar: AvatarUrlsSchema.optional(),
    isOnline: z.boolean().optional(),
    lastSeenAt: z.string().nullable().optional(),
    shared: UserCardSharedSchema.optional(),
    points: UserCardPointsSchema.optional(),
    favoriteGame: GameTypeSchema.optional(),
});
//# sourceMappingURL=identity.js.map